import { CopyOutlined } from '@ant-design/icons'
import { Stack, TextColors, Typography } from '@sureco/design-system'
import { Skeleton, Switch } from 'antd'
import { SwitchChangeEventHandler } from 'antd/es/switch'
import CopyToClipboard from 'components/CopyToClipboard/CopyToClipboard'

import React, { memo } from 'react'
import useCustomerConfiguration, {
  EMAIL_NOTIFICATION_KEYS,
} from '../hooks/useCustomerConfiguration'

const CustomDetailConfigurationSkeleton = memo(() => {
  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
      }}
    >
      <Skeleton />
    </div>
  )
})

const CustomerDetailConfigurationSwitch = memo(
  function CustomerDetailConfigurationSwitch({
    label,
    isPending,
    isDisabled,
    value,
    onChange,
  }: {
    label: string
    isPending: boolean
    isDisabled: boolean
    value?: boolean
    onChange?: SwitchChangeEventHandler
  }) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Switch
          loading={isPending}
          disabled={isPending || isDisabled}
          value={value}
          onChange={onChange}
        />
        <Typography size={16} color="#003146">
          {label}{' '}
          <Typography
            color={value ? TextColors.primary : TextColors.error}
            size={16}
            bold
          >
            {value ? 'ON' : 'OFF'}
          </Typography>
        </Typography>
      </div>
    )
  }
)

const CustomerDetailConfigurationContext = memo(
  function CustomerDetailConfigurationContext({
    customer,
    configuration,
    isLoading,
    isError,
  }: {
    customer: any
    configuration: any
    isLoading: boolean
    isError: boolean
  }) {
    /* --------------------------------- States --------------------------------- */
    const {
      updateIchraNoticeMutation,
      updateWelcomeEmailMutation,
      updateReminderEmailMutation,
      updateCMNavigationMutation,
      updateEmailNotificationMutation,
      updateERContribution: {
        updateERContributionData,
        isUpdatingERContribution,
      },
    } = useCustomerConfiguration(customer.id, customer?.name)
    const { mutate: updateIchraNotice, isPending: isUpdatingIchraNotice } =
      updateIchraNoticeMutation()
    const { mutate: updateWelcomeEmail, isPending: isUpdatingWelcomeEmail } =
      updateWelcomeEmailMutation()
    const { mutate: updateReminderEmail, isPending: isUpdatingReminderEmail } =
      updateReminderEmailMutation()
    const { mutate: updateCMNavigation, isPending: isUpdatingCMNavigation } =
      updateCMNavigationMutation()
    const {
      mutate: updateEmailNotification,
      isPending: isUpdatingEmailNotification,
      variables: emailNotificationVariables,
    } = updateEmailNotificationMutation()
    const stripeLinkText = 'https://shorturl.at/Z0hB2123' // Hardcoded for now, follow previous implementation

    return (
      <div
        style={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '2rem',
        }}
      >
        <CustomerDetailConfigurationSwitch
          label="ICHRA Notices"
          isPending={isUpdatingIchraNotice}
          isDisabled={isLoading || isError}
          value={configuration?.isIchraNoticeEnabled}
          onChange={() => {
            updateIchraNotice({
              isIchraNoticeEnabled: !configuration?.isIchraNoticeEnabled,
            })
          }}
        />
        <CustomerDetailConfigurationSwitch
          label="Welcome Email"
          isPending={isUpdatingWelcomeEmail}
          isDisabled={isLoading || isError}
          value={configuration?.isWelcomeEmailEnabled}
          onChange={() => {
            updateWelcomeEmail({
              isWelcomeEmailEnabled: !configuration?.isWelcomeEmailEnabled,
            })
          }}
        />
        <CustomerDetailConfigurationSwitch
          label="Reminder Emails"
          isPending={isUpdatingReminderEmail}
          isDisabled={isLoading || isError || isUpdatingReminderEmail}
          value={configuration?.isEnrollmentReminderEmailEnabled ?? true}
          onChange={() => {
            updateReminderEmail({
              isEnrollmentReminderEmailEnabled: !(
                configuration?.isEnrollmentReminderEmailEnabled ?? true
              ),
            })
          }}
        />
        <CustomerDetailConfigurationSwitch
          label="Enrollment Confirmation Emails"
          isPending={
            isUpdatingEmailNotification &&
            emailNotificationVariables?.key ===
              EMAIL_NOTIFICATION_KEYS.enrollmentConfirmation
          }
          isDisabled={isLoading || isError || isUpdatingEmailNotification}
          value={configuration?.isEnrollmentConfirmationEmailEnabled ?? true}
          onChange={() => {
            updateEmailNotification({
              key: EMAIL_NOTIFICATION_KEYS.enrollmentConfirmation,
              value: !(
                configuration?.isEnrollmentConfirmationEmailEnabled ?? true
              ),
              label: 'Enrollment Confirmation Emails',
            })
          }}
        />
        <CustomerDetailConfigurationSwitch
          label="Application Verified Emails"
          isPending={
            isUpdatingEmailNotification &&
            emailNotificationVariables?.key ===
              EMAIL_NOTIFICATION_KEYS.applicationVerified
          }
          isDisabled={isLoading || isError || isUpdatingEmailNotification}
          value={configuration?.isApplicationVerifiedEmailEnabled ?? true}
          onChange={() => {
            updateEmailNotification({
              key: EMAIL_NOTIFICATION_KEYS.applicationVerified,
              value: !(
                configuration?.isApplicationVerifiedEmailEnabled ?? true
              ),
              label: 'Application Verified Emails',
            })
          }}
        />
        <CustomerDetailConfigurationSwitch
          label="CMv1 Navigation Link"
          isPending={isUpdatingCMNavigation}
          isDisabled={isLoading || isError}
          value={configuration?.isCMv1Enabled}
          onChange={() => {
            updateCMNavigation({
              isCMNavigationEnabled: !configuration?.isCMv1Enabled,
              version: 'v1',
            })
          }}
        />
        <CustomerDetailConfigurationSwitch
          label="CMv2 Navigation Link"
          isPending={isUpdatingCMNavigation}
          isDisabled={isLoading || isError}
          value={configuration?.isCMv2Enabled}
          onChange={() => {
            updateCMNavigation({
              isCMNavigationEnabled: !configuration?.isCMv2Enabled,
              version: 'v2',
            })
          }}
        />
        <CustomerDetailConfigurationSwitch
          label="Display ER Contribution"
          isPending={isUpdatingERContribution}
          isDisabled={isLoading || isError}
          value={configuration?.displayContributionsToEmployees}
          onChange={() => {
            updateERContributionData(
              !configuration?.displayContributionsToEmployees
            )
          }}
        />
        {configuration?.stripeLink && (
          <Stack
            direction="row"
            justify="space-between"
            align="center"
            space={10}
          >
            <Typography size={14} color={TextColors.primary}>
              Stripe Link
              <CopyToClipboard
                onCopy={undefined}
                text={configuration?.stripeLink}
                icon={false}
              >
                <Typography size={14} color="#999">
                  {stripeLinkText} <CopyOutlined />
                </Typography>
              </CopyToClipboard>
            </Typography>
          </Stack>
        )}
      </div>
    )
  }
)

const CustomerDetailConfiguration = React.memo(
  function CustomerDetailConfiguration({ customer }: { customer: any }) {
    /* --------------------------------- States --------------------------------- */
    const { queryToggles } = useCustomerConfiguration(customer.id)
    const { isLoading, data: configuration, isError } = queryToggles()

    /* ---------------------------------- Doms ---------------------------------- */
    const Context = (
      <CustomerDetailConfigurationContext
        customer={customer}
        configuration={configuration}
        isLoading={isLoading}
        isError={isError}
      />
    )
    const Loading = <CustomDetailConfigurationSkeleton />

    return (
      <Stack
        direction="row"
        align="center"
        space={10}
        style={{ justifyContent: 'space-between', gap: '2rem' }}
      >
        {isLoading ? Loading : Context}
      </Stack>
    )
  }
)

export default CustomerDetailConfiguration
